import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../../context/GameContext';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiCamera } = FiIcons;

const EMOTIONS = [
  { name: 'happy', emoji: '😊', color: 'bg-yellow-100 border-yellow-300', desc: 'Feeling good and smiling!' },
  { name: 'sad', emoji: '😢', color: 'bg-blue-100 border-blue-300', desc: 'When tears come and we feel down' },
  { name: 'angry', emoji: '😠', color: 'bg-red-100 border-red-300', desc: 'When we feel mad or frustrated' },
  { name: 'scared', emoji: '😨', color: 'bg-purple-100 border-purple-300', desc: 'When something surprises or frightens us' },
  { name: 'surprised', emoji: '😲', color: 'bg-orange-100 border-orange-300', desc: 'When something unexpected happens' },
  { name: 'confused', emoji: '😕', color: 'bg-gray-100 border-gray-300', desc: 'When we don\'t understand', unlock: 3 },
  { name: 'proud', emoji: '😌', color: 'bg-green-100 border-green-300', desc: 'When we did something great!', unlock: 5 },
  { name: 'shy', emoji: '😳', color: 'bg-pink-100 border-pink-300', desc: 'When we feel a little nervous', unlock: 7 }
];

const STORIES = [
  { text: "Mia lost her favorite teddy bear", emotion: 'sad', emoji: '🧸' },
  { text: "Jamie got a perfect score on their test!", emotion: 'proud', emoji: '📝' },
  { text: "A big dog suddenly barked loudly", emotion: 'scared', emoji: '🐕' },
  { text: "Someone took Alex's toy without asking", emotion: 'angry', emoji: '🧸' },
  { text: "It's Sam's birthday party today!", emotion: 'happy', emoji: '🎂' },
  { text: "The magic trick worked perfectly!", emotion: 'surprised', emoji: '🎩' },
  { text: "Taylor doesn't understand the homework", emotion: 'confused', emoji: '📚' },
  { text: "A new kid wants to play but Casey feels nervous", emotion: 'shy', emoji: '🤝' },
  { text: "The ice cream fell on the ground", emotion: 'sad', emoji: '🍦' },
  { text: "Grandma is coming to visit!", emotion: 'happy', emoji: '👵' }
];

export default function SELFeelingsFaces() {
  const navigate = useNavigate();
  const { dispatch } = useGame();
  const [level, setLevel] = useState(1);
  const [currentStory, setCurrentStory] = useState(0);
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraChallenge, setCameraChallenge] = useState(null);
  const videoRef = useRef(null);

  const availableEmotions = EMOTIONS.filter(e => !e.unlock || level >= e.unlock);
  const story = STORIES[currentStory];

  const handleEmotionSelect = (emotion) => {
    setSelectedEmotion(emotion);
    setShowFeedback(true);
    
    if (emotion.name === story.emotion) {
      setScore(s => s + 10);
      dispatch({ type: 'ADD_SCORE', payload: 10 });
    }
  };

  const nextStory = () => {
    if (currentStory < Math.min(STORIES.length - 1, level * 3 - 1)) {
      setCurrentStory(c => c + 1);
      setSelectedEmotion(null);
      setShowFeedback(false);
      setShowCamera(false);
    } else if (level < 3) {
      setLevel(l => l + 1);
      setCurrentStory(0);
      setSelectedEmotion(null);
      setShowFeedback(false);
      setShowCamera(false);
    } else {
      // Game complete
      dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'sel-feelings-faces', bonusTokens: score / 2 + 20 } });
      navigate('/dashboard');
    }
  };

  const startCameraChallenge = () => {
    setCameraChallenge(story.emotion);
    setShowCamera(true);
    // In a real implementation, this would access the camera
    setTimeout(() => {
      setShowCamera(false);
      nextStory();
    }, 3000);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen p-4 pb-24 bg-gradient-to-br from-yellow-50 to-orange-50">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 bg-white rounded-2xl px-4 py-2 shadow border-2 border-yellow-200">
            <SafeIcon icon={FiArrowLeft} /> Back
          </button>
          <div className="bg-white rounded-2xl px-4 py-2 shadow border-2 border-yellow-200">
            <span className="font-kid text-yellow-700">Level {level} 🌟</span>
          </div>
          <div className="bg-yellow-100 rounded-2xl px-4 py-2 shadow border-2 border-yellow-300">
            <span className="font-kid text-yellow-700">Score: {score}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="h-4 bg-yellow-100 rounded-full mb-8 overflow-hidden border-2 border-yellow-200">
          <motion.div 
            animate={{ width: `${((currentStory % 3 + 1) / 3) * 100}%` }}
            className="h-full bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full"
          />
        </div>

        {/* Story Card */}
        <motion.div 
          key={currentStory}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-yellow-200 mb-8 text-center"
        >
          <div className="text-6xl mb-4">{story.emoji}</div>
          <h2 className="font-comic text-xl text-gray-700 mb-2">How does this story make you feel?</h2>
          <p className="font-kid text-2xl text-yellow-700 mb-6">"{story.text}"</p>
          
          {/* Character hint */}
          <div className="bg-yellow-50 rounded-2xl p-4 inline-block">
            <span className="font-comic text-sm text-yellow-600">Help {story.emoji} find the right feeling!</span>
          </div>
        </motion.div>

        {/* Emotion Grid */}
        {!showFeedback ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {availableEmotions.map((emotion, idx) => (
              <motion.button
                key={emotion.name}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleEmotionSelect(emotion)}
                className={`${emotion.color} rounded-2xl p-6 border-4 shadow-lg text-center transition-all`}
              >
                <div className="text-5xl mb-2">{emotion.emoji}</div>
                <span className="font-kid text-lg capitalize">{emotion.name}</span>
              </motion.button>
            ))}
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-3xl p-8 text-center border-4 ${selectedEmotion?.name === story.emotion ? 'bg-green-50 border-green-300' : 'bg-orange-50 border-orange-300'}`}
          >
            <div className="text-6xl mb-4">
              {selectedEmotion?.name === story.emotion ? '🎉' : '💡'}
            </div>
            <h3 className="font-kid text-2xl mb-2">
              {selectedEmotion?.name === story.emotion ? 'Perfect Match!' : 'Good Try!'}
            </h3>
            <p className="font-comic text-gray-600 mb-4">
              {selectedEmotion?.name === story.emotion 
                ? `Yes! "${story.text}" makes us feel ${story.emotion}.`
                : `Actually, "${story.text}" usually makes us feel ${story.emotion}.`
              }
            </p>
            
            {selectedEmotion?.name === story.emotion && !showCamera && (
              <div className="bg-white rounded-2xl p-4 mb-4 border-2 border-yellow-200">
                <p className="font-comic text-sm text-gray-600 mb-2">🎭 Mirror Challenge!</p>
                <p className="font-kid text-yellow-700">Can YOU make a {story.emotion} face?</p>
                <button 
                  onClick={startCameraChallenge}
                  className="mt-3 bg-yellow-400 text-white px-4 py-2 rounded-xl font-kid flex items-center gap-2 mx-auto"
                >
                  <SafeIcon icon={FiCamera} /> Try It!
                </button>
              </div>
            )}

            {showCamera && (
              <div className="bg-black rounded-2xl p-4 mb-4 aspect-video flex items-center justify-center">
                <div className="text-white text-center">
                  <div className="text-4xl mb-2">📸</div>
                  <p className="font-comic">Make a {cameraChallenge} face!</p>
                  <p className="text-sm font-comic opacity-70 mt-2">(Camera simulation)</p>
                </div>
              </div>
            )}

            {!showCamera && (
              <button 
                onClick={nextStory}
                className="bg-yellow-500 text-white px-6 py-3 rounded-2xl font-kid hover:bg-yellow-600 transition-colors"
              >
                Continue →
              </button>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}